import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { API } from "@/contexts/AuthContext";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const tokenMissing = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setDone(true);
      toast.success("Password reset! Signing you in...");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003858] via-[#006080] to-[#119AB3] flex items-center justify-center p-6 relative overflow-hidden" data-testid="reset-password-page">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#B7EB1B]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#119AB3]/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6 text-afa-lime" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-white leading-tight" style={{fontFamily: 'Manrope'}}>Simply Smarter</span>
              <span className="text-base font-medium text-afa-lime leading-tight">Email</span>
            </div>
          </div>

          {tokenMissing && (
            <div className="text-center" data-testid="reset-token-missing">
              <div className="w-14 h-14 mx-auto rounded-full bg-rose-500/20 border border-rose-300/30 flex items-center justify-center mb-4">
                <AlertCircle className="w-7 h-7 text-rose-300" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Manrope'}}>Link is missing the reset token</h1>
              <p className="text-white/70 mb-6 leading-relaxed">
                Please use the full link from your reset email. If you no longer have it, you can request a new one.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full h-12 bg-afa-lime hover:bg-afa-lime/90 text-afa-navy rounded-full font-semibold"
              >
                Request a new link
              </Button>
            </div>
          )}

          {!tokenMissing && done && (
            <div className="text-center" data-testid="reset-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-afa-lime/20 border border-afa-lime/40 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7 text-afa-lime" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Manrope'}}>Password reset!</h1>
              <p className="text-white/70">Redirecting you to sign in...</p>
            </div>
          )}

          {!tokenMissing && !done && (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white" style={{fontFamily: 'Manrope'}}>Choose a new password</h1>
                <p className="text-white/70 mt-2">Make it something you'll actually remember.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-white/90 mb-2 block">New password</Label>
                  <Input
                    data-testid="reset-new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-afa-lime focus:ring-afa-lime"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-white/90 mb-2 block">Confirm password</Label>
                  <Input
                    data-testid="reset-confirm-password"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Type it again"
                    className="h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-afa-lime focus:ring-afa-lime"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
                <Button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-afa-lime hover:bg-afa-lime/90 text-afa-navy rounded-full font-semibold text-base mt-6"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Reset password"}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-white/50 text-sm mt-6">
          Simply Smarter Email · Built by Anne Flinn Advertising
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
